import './adaptive-card.scss';

import { Pagination } from 'antd';
import dayjs from 'dayjs';

import TableDate from '@/components/Shared/TableData/TableDate';
import { AllurLoader, ChosenBankStatus } from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { getUserInitials } from '@/services/helpers/strings';
import { useAppSelector } from '@/store';
import type { TDataSource } from '@/types/dataSource';

import { RenderBankLogo, StatusTooltip } from '../../ui';
import type { ResponsesInterface } from '../../widgets/table-columns/types';

interface AdaptiveAutoCreditProps {
  data: TDataSource[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  isLoading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onClick: (row: TDataSource) => void;
}
const AdaptiveAutoCredit = ({
  data,
  pagination,
  isLoading,
  onPageChange,
  onClick
}: AdaptiveAutoCreditProps) => {
  const [windowWidth] = useWindowSize();
  const { dicts } = useAppSelector((state) => state.dict);

  const banksDict = dicts?.[DictTypes.Banks];
  const { current, pageSize, total } = pagination;

  return (
    <>
      {isLoading ? (
        <AllurLoader fullWidth />
      ) : (
        <>
          <div className="adaptive-card">
            {!data || data?.length === 0 ? (
              <p className="adaptive-card__no-data">Нет заявок </p>
            ) : (
              data?.map((record) => {
                const bank = banksDict?.getBy.name[record.status.selectedBank];
                const bankIconAndName =
                  BANKS_ICONS_NAMES[
                    record.status.selectedBank as keyof typeof BANKS_ICONS_NAMES
                  ];

                const responses: ResponsesInterface[] = [];

                let hasBankResponses = false;

                if (
                  record.status.approved.length > 0 ||
                  record.status.leasingStatus.approved.length > 0
                ) {
                  responses.push({
                    banks: record.status.approved,
                    leasings: record.status.leasingStatus.approved,
                    color: '#0CAF60',
                    type: 'approved',
                    tooltipTitle: 'Одобрено предложение'
                  });
                  hasBankResponses ||= record.status.approved.length > 0;
                }

                const activeExpectationsBanks =
                  record.status.expectations.filter(
                    (item) => banksDict?.getBy.name[item.bank]?.is_active
                  );
                if (
                  activeExpectationsBanks.length > 0 ||
                  record.status.leasingStatus.expectations.length > 0
                ) {
                  responses.push({
                    banks: activeExpectationsBanks,
                    leasings: record.status.leasingStatus.expectations,
                    color: '#ffd023',
                    type: 'waiting',
                    tooltipTitle: 'Ожидают ответа'
                  });
                  hasBankResponses ||= activeExpectationsBanks.length > 0;
                }

                if (record.status.approvedAlternatives.length > 0) {
                  responses.push({
                    banks: record.status.approvedAlternatives,
                    leasings: [],
                    color: '#2f78ee',
                    type: 'alternative',
                    tooltipTitle: 'Одобрена альтернатива'
                  });
                  hasBankResponses ||=
                    record.status.approvedAlternatives.length > 0;
                }

                if (
                  record.status.denied.length > 0 ||
                  record.status.leasingStatus.denied.length > 0
                ) {
                  responses.push({
                    banks: record.status.denied,
                    leasings: record.status.leasingStatus.denied,
                    color: '#e03137',
                    type: 'decline',
                    tooltipTitle: 'Отклонено'
                  });
                  hasBankResponses ||= record.status.denied.length > 0;
                }

                if (
                  record.status.errors.length > 0 ||
                  record.status.leasingStatus.errors.length > 0
                ) {
                  responses.push({
                    banks: record.status.errors,
                    leasings: record.status.leasingStatus.errors,
                    color: '#a8a8a8',
                    type: 'error',
                    tooltipTitle: 'Ошибка банка'
                  });
                  hasBankResponses ||= record.status.errors.length > 0;
                }

                return (
                  <div
                    key={record.id}
                    onClick={() => onClick(record)}
                    className={`
                      adaptive-card__card ${record.status.isNotTakenOnline ? 'adaptive-card__card--online' : ''}`}
                  >
                    <div className="adaptive-card__card-header">
                      <div
                        className={`
                        adaptive-card__avatar ${record.status.isNotTakenOnline ? 'adaptive-card__avatar--online' : ''}`}
                      >
                        {record?.client?.fullName[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="adaptive-card__client-name">
                          {getUserInitials(record?.client?.fullName)}
                        </p>
                        <p className="adaptive-card__client-phone">
                          {record?.loan?.auto === ' undefined, ' ||
                          !record?.loan?.auto
                            ? 'Нет данных о машине'
                            : record.loan.auto}
                        </p>
                      </div>
                      <span
                        className={`adaptive-card__request-type ${
                          record.isOnlineRequest
                            ? 'adaptive-card__request-type--online'
                            : 'adaptive-card__request-type--offline'
                        }`}
                      >
                        {record.isOnlineRequest ? 'Онлайн' : 'Офлайн'}
                      </span>
                    </div>
                    <div className="adaptive-card__card-extra">
                      {record.status.code === 'biometrics_failed' ? (
                        <div className="adaptive-card__big-status adaptive-card__big-status--error">
                          <p className="adaptive-card__bank-text adaptive-card__bank-text--center">
                            {record.status.name}
                          </p>
                        </div>
                      ) : record.status.isFinanced ? (
                        <ChosenBankStatus
                          img={bank?.icon || bankIconAndName?.icon}
                          name={bank?.fullname || bankIconAndName?.name}
                          status={{
                            code: 'financed',
                            name: 'Профинансировано'
                          }}
                        />
                      ) : record.status.isSelected ? (
                        <ChosenBankStatus
                          img={bank?.icon || bankIconAndName?.icon}
                          name={bank?.fullname || bankIconAndName?.name}
                          status={{ code: 'chosen' }}
                        />
                      ) : record.status.leasingStatus.isFinanced ? (
                        <ChosenBankStatus
                          img={
                            record.status.leasingStatus.selectedLeasing
                              ?.leasingCompany.icon || ''
                          }
                          name={
                            record.status.leasingStatus.selectedLeasing
                              ?.leasingCompany.name || ''
                          }
                          status={{
                            code: 'financed',
                            name: 'Профинансировано'
                          }}
                          isLeasing
                        />
                      ) : record.status.leasingStatus.isSelected ? (
                        <ChosenBankStatus
                          img={
                            record.status.leasingStatus.selectedLeasing
                              ?.leasingCompany.icon || ''
                          }
                          name={
                            record.status.leasingStatus.selectedLeasing
                              ?.leasingCompany.name || ''
                          }
                          status={{ code: 'chosen', name: 'Лизинг выбран' }}
                          isLeasing
                        />
                      ) : (
                        <div className="adaptive-card__statuses-wrapper">
                          {responses.length > 0 ? (
                            responses.map((item) => {
                              const isWaiting = item.type === 'waiting';
                              return (
                                <StatusTooltip
                                  key={item.color}
                                  title={item.tooltipTitle}
                                  textColor={item.color}
                                  titleChildren={
                                    <>
                                      {isWaiting && (
                                        <TableDate
                                          date={record.created_at.date}
                                        />
                                      )}
                                      {item.banks.map((res, index) => (
                                        <div
                                          className="auto-credit-table__tooltip-bank"
                                          key={`${res.status} ${index}`}
                                        >
                                          <RenderBankLogo
                                            selectedBank={res.bank}
                                          />
                                          <div className="auto-credit-table__tooltip-bank-content">
                                            <div className="auto-credit-table__tooltip-bank-header">
                                              <span className="auto-credit-table__tooltip-bank-name">
                                                {banksDict?.getBy.name[res.bank]
                                                  ?.fullname || ''}
                                              </span>
                                            </div>
                                            <span className="auto-credit-table__tooltip-time">
                                              от{' '}
                                              {dayjs(
                                                res.responseDate || null
                                              ).format('DD.MM.YYYY, HH:mm')}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                      {item.leasings.map((res, index) => {
                                        return (
                                          <div
                                            className="auto-credit-table__tooltip-bank"
                                            key={`${res.status} ${index} leasing`}
                                          >
                                            <RenderBankLogo
                                              src={
                                                res.leasingCompany.icon || ''
                                              }
                                            />
                                            <div className="auto-credit-table__tooltip-bank-content">
                                              <div className="auto-credit-table__tooltip-bank-header">
                                                <span className="auto-credit-table__tooltip-bank-name">
                                                  {res.leasingCompany.name}
                                                </span>
                                                <span className="auto-credit-table__tooltip-leasing-badge">
                                                  Лизинг
                                                </span>
                                              </div>
                                              <span className="auto-credit-table__tooltip-time">
                                                от{' '}
                                                {dayjs(
                                                  res.createdAt || null
                                                ).format('DD.MM.YYYY, HH:mm')}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </>
                                  }
                                >
                                  <div
                                    style={{ background: item.color }}
                                    className="adaptive-card__status"
                                  >
                                    <span
                                      className={`adaptive-card__bank-text ${
                                        isWaiting
                                          ? 'adaptive-card__bank-text--dark'
                                          : ''
                                      }`}
                                    >
                                      {(item.banks.length ?? 0) +
                                        (item.leasings.length ?? 0) || ''}
                                    </span>
                                  </div>
                                </StatusTooltip>
                              );
                            })
                          ) : record.status.isAddedToQueue ? (
                            <StatusTooltip title={'В процессе оформления'}>
                              <div className="adaptive-card__big-status">
                                <p className="adaptive-card__bank-text adaptive-card__bank-text--center">
                                  {record.status.name}
                                </p>
                              </div>
                            </StatusTooltip>
                          ) : record.status.isDraft ? (
                            <StatusTooltip title={'В процессе оформления'}>
                              <div className="adaptive-card__big-status">
                                <p className="adaptive-card__bank-text adaptive-card__bank-text--center">
                                  {record.status.name}
                                </p>
                              </div>
                            </StatusTooltip>
                          ) : (
                            record.status.isUnverified && (
                              <StatusTooltip title={'В процессе валидации ОТП'}>
                                <div className="adaptive-card__big-status adaptive-card__big-status--unverified">
                                  <p className="adaptive-card__bank-text adaptive-card__bank-text--center">
                                    {record.status.name}
                                  </p>
                                </div>
                              </StatusTooltip>
                            )
                          )}
                          {!hasBankResponses &&
                            record.sentTo !== 'leasing' &&
                            !record.status.isAddedToQueue &&
                            !record.status.isDraft &&
                            !record.status.isUnverified &&
                            !record.status.isError && (
                              <div
                                style={{ background: '#FFD023' }}
                                className="adaptive-card__status"
                              >
                                <p className="adaptive-card__bank-text adaptive-card__bank-text--dark">
                                  {
                                    banksDict?.content.filter(
                                      (item) => item.is_active
                                    ).length
                                  }
                                </p>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <Pagination
              className="adaptive-card__pagination"
              current={current}
              pageSize={pageSize}
              total={total}
              showSizeChanger={false}
              simple={windowWidth <= 560}
              showLessItems={windowWidth <= 560}
              onChange={onPageChange}
            />
          </div>
        </>
      )}
    </>
  );
};
export default AdaptiveAutoCredit;
